import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import apiClient from '../../../api/client';
import PhoneVerificationModal from '../../../components/phone-verification-modal';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '../../../components/ui/input-otp';
import { FaceVerifyScreens } from '../../../constants/face.const';
import { useApp as portalApp } from '../../../context/usePortal';
import FailedSVG from '../../register/FailedScreen/components/FailedSvg';

enum PinUpdateFlowStep {
  EnterNewPin,
  ConfirmPin,
  Success
}

const formSchema = z.object({
  newPin: z.string().min(6, 'PIN must be at least 6 characters'),
  confirmPin: z.string().min(6, 'PIN must be at least 6 characters')
});

type FormSchema = z.infer<typeof formSchema>;

interface IProps {
  lastUpdated: string | null;
}

const ResetPin: React.FC<IProps> = ({ lastUpdated }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [verifyPhone, setVerifyPhone] = useState<boolean>(false);
  const [showFaceVerificationError, setShowFaceVerificationError] =
    useState(false);
  const { user } = portalApp();
  const [step, setStep] = useState(PinUpdateFlowStep.EnterNewPin);
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newPin: '',
      confirmPin: ''
    }
  });

  const phoneNumber = `${user?.country_code}-${user?.user_phone_number}`;

  // Handle face verification return
  const isVerified = searchParams.get('isVerified') === 'true';
  const isVerificationFailed = searchParams.get('isVerified') === 'false';
  const flowType = searchParams.get('flow');

  useEffect(() => {
    if (isVerified && flowType === FaceVerifyScreens.RESET_PIN) {
      // Face verification successful (from ChangePhoneNumber flow), proceed to phone verification
      setOpenDialog(true);
      setVerifyPhone(true);
      const params = new URLSearchParams(window.location.search);
      params.delete('isVerified');
      params.delete('flow');
      setSearchParams(params);
    } else if (
      isVerificationFailed &&
      flowType === FaceVerifyScreens.RESET_PIN
    ) {
      // Face verification failed (from ChangePhoneNumber flow), show error dialog and stop the flow
      setOpenDialog(true);
      setShowFaceVerificationError(true);
      const params = new URLSearchParams(window.location.search);
      params.delete('isVerified');
      params.delete('flow');
      setSearchParams(params);
    }
  }, [isVerified, isVerificationFailed, flowType, setSearchParams]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setStep(PinUpdateFlowStep.EnterNewPin);
    setVerifyPhone(false);
    setShowFaceVerificationError(false);
    form.reset();
    // Clear any URL parameters when closing
    const params = new URLSearchParams(window.location.search);
    params.delete('isVerified');
    params.delete('flow');
    setSearchParams(params);
  };

  const handleNewPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }

    setStep(PinUpdateFlowStep.ConfirmPin);
  };

  const handleConfirmPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }

    onSubmit(form.getValues());
  };

  const handleStartAgain = () => {
    form.reset();
    setStep(PinUpdateFlowStep.EnterNewPin);
  };

  const onSubmit = (data: FormSchema) => {
    if (data.newPin !== data.confirmPin) {
      form.setError('confirmPin', {
        type: 'manual',
        message: 'PINs do not match'
      });
      return;
    }

    // For normal reset PIN flow, go directly to phone verification
    setVerifyPhone(true);
  };

  const resetPin = useMutation({
    mutationFn: async ({ newPin, otp }: { newPin: string; otp: string }) => {
      await apiClient.post('/users/reset-pin', {
        newPin,
        otp
      });
    },
    onSuccess: () => {
      toast.success('PIN reset successfully');

      setVerifyPhone(false);
      setStep(PinUpdateFlowStep.Success);
    },
    onError: (error) => {
      console.error('Error resetting PIN:', error);
      if (
        (error as any)?.response?.data?.error?.code === 'OTP_FAILED_ATTEMPTS'
      ) {
        return;
      }
      toast.error(
        (error as any)?.response?.data?.error?.message ||
          'Failed to reset PIN. Please try again.'
      );
    }
  });

  const handleSubmitChangePin = (otp: string) => {
    resetPin.mutate({
      newPin: form.getValues().newPin,
      otp
    });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-t-1 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-gray-900">Reset PIN</h3>
          <p className="text-sm text-gray-500">Update your existing PIN.</p>
          {lastUpdated && (
            <p className="text-sm italic text-gray-500">
              PIN last updated: {dayjs(lastUpdated).format('MMMM DD, YYYY')}
            </p>
          )}
        </div>
        <Button
          type="button"
          className="min-w-24 disabled:bg-gray-700"
          onClick={() => {
            setOpenDialog(true);
          }}
        >
          Change
        </Button>
      </div>

      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          {(step === PinUpdateFlowStep.EnterNewPin ||
            step === PinUpdateFlowStep.ConfirmPin) && (
            <DialogHeader className="mt-5">
              <DialogTitle className="text-center">
                {step === PinUpdateFlowStep.EnterNewPin
                  ? 'Please Enter New 6-Digit PIN'
                  : step === PinUpdateFlowStep.ConfirmPin
                    ? 'Enter New PIN Again'
                    : 'PIN Reset Successful'}
              </DialogTitle>
            </DialogHeader>
          )}

          {step !== PinUpdateFlowStep.Success ? (
            <>
              <p className="text-center text-sm text-foreground/90">
                Please {step === PinUpdateFlowStep.EnterNewPin ? '' : 're-'}
                enter your 6-digit PIN
              </p>

              <form className="mb-10 flex flex-col items-center">
                {step === PinUpdateFlowStep.EnterNewPin && (
                  <>
                    <Controller
                      name={'newPin'}
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          {...field}
                          maxLength={6}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            handleNewPin(e);
                          }}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {form.formState.errors.confirmPin && (
                      <span className="mt-2 text-xs text-red-500">
                        {form.formState.errors.confirmPin.message as string}
                      </span>
                    )}
                  </>
                )}

                {step === PinUpdateFlowStep.ConfirmPin && (
                  <>
                    <Controller
                      name={'confirmPin'}
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          {...field}
                          maxLength={6}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            handleConfirmPin(e);
                          }}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={1} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={4} />
                          </InputOTPGroup>
                          <InputOTPGroup>
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {form.formState.errors.confirmPin && (
                      <span className="mt-2 text-xs text-red-500">
                        {form.formState.errors.confirmPin.message as string}
                      </span>
                    )}
                  </>
                )}
              </form>

              {step === PinUpdateFlowStep.ConfirmPin && (
                <Button
                  variant={'link'}
                  className="decoration-[#5B5959]"
                  onClick={handleStartAgain}
                >
                  <p className="text-[#5B5959]">Start Again?</p>
                </Button>
              )}
            </>
          ) : (
            <div className="mb-1 mt-3 flex flex-col items-center">
              <img
                src="/assets/images/update-phone-success.png"
                alt="Phone Update Success"
                className="mx-auto w-32"
              />

              <h3 className="mt-4 text-center text-sm font-medium">
                Your PIN has been updated successfully
              </h3>

              <Button
                className="mx-4 mt-6 w-4/5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
                onClick={handleCloseDialog}
                type="button"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PhoneVerificationModal
        phoneNumber={phoneNumber}
        onClose={() => {
          setVerifyPhone(false);
        }}
        onVerify={(otp) => {
          handleSubmitChangePin(otp);
        }}
        isOpen={verifyPhone}
        verifyError={resetPin.error}
        screen={FaceVerifyScreens.RESET_PIN}
        disableCannotAccessOldPhone={true}
      />

      {/* Face Verification Error Dialog */}
      <Dialog
        open={showFaceVerificationError}
        onOpenChange={setShowFaceVerificationError}
      >
        <DialogContent className="max-w-[24rem] rounded-3xl">
          <div className="mb-2 flex flex-col items-center">
            <FailedSVG className="-mt-4 h-40 w-40" />

            <p className="mx-4 mb-4 mt-3 text-center text-sm font-medium text-foreground/90">
              Failed to verify face. Please try again or contact support if the
              issue persists.
            </p>

            <Button
              className="mx-4 mt-4 w-4/5 rounded-full bg-[#5CB891] py-2 text-sm font-medium hover:bg-[#5CB891]"
              onClick={handleCloseDialog}
              type="button"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResetPin;
